import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@1trading/react';
import { ArrowRight } from 'lucide-react';
import { MS_PER_DAY, toUtcDateKey, utcDayMs } from '@/utils/date';
import { formatUtcMonth } from '@/utils/format';
import { type ScheduleBar, type ScheduleStatus, scheduleBars } from './mm-program-editing';
import type { ProgramInput } from './mm-program-schema';

// An open schedule runs past the last date shown, so the axis keeps some room after today and the last change.
const MIN_TAIL_DAYS = 14;
const TAIL_SHARE = 0.15;
const MAX_MONTH_TICKS = 8;
// Month labels are centred on their day, so ones this close to an edge would hang outside the frame.
const TICK_EDGE_PERCENT = 4;

export const STATUS_META: Record<ScheduleStatus, { label: string; className: string }> = {
  scored: { label: 'Scored', className: 'border-border bg-muted text-muted-foreground' },
  endedToday: { label: 'Ended today', className: 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  inForce: { label: 'In force', className: 'border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  upcoming: { label: 'Upcoming', className: 'border-blue-500/50 bg-blue-500/10 text-blue-700 dark:text-blue-400' },
};
const STATUS_ORDER: ScheduleStatus[] = ['scored', 'endedToday', 'inForce', 'upcoming'];

const DAY_FORMAT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const describeBar = (bar: ScheduleBar) => {
  const span = `${DAY_FORMAT.format(utcDayMs(bar.start))} to ${bar.end ? `${DAY_FORMAT.format(utcDayMs(bar.end) - MS_PER_DAY)} (last day)` : 'open-ended'}`;
  return `${span} · ${STATUS_META[bar.status].label}${bar.sessioned ? ' · Core, Extended and Weekend set' : ''}`;
};

const monthTicks = (from: number, to: number) => {
  const start = new Date(from);
  const end = new Date(to);
  const count = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + end.getUTCMonth() - start.getUTCMonth();
  const months = Array.from({ length: Math.max(count, 0) }, (_, index) => Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1 + index, 1));
  const step = Math.ceil(months.length / MAX_MONTH_TICKS);
  return months.filter((_, index) => index % step === 0);
};

interface ScheduleTimelineProps {
  schedules: ProgramInput[];
  /** Positions of the schedule or session set being edited */
  selected: number[];
  onSelect: (index: number) => void;
}

export const ScheduleTimeline = ({ schedules, selected, onSelect }: ScheduleTimelineProps) => {
  const bars = scheduleBars(schedules).filter((bar) => bar.start);
  if (!bars.length) return null;
  const today = utcDayMs(toUtcDateKey(new Date()));
  const from = Math.min(today, ...bars.map((bar) => utcDayMs(bar.start)));
  const lastChange = Math.max(today, ...bars.flatMap((bar) => [utcDayMs(bar.start), bar.end ? utcDayMs(bar.end) : 0]));
  const to = lastChange + Math.max(MIN_TAIL_DAYS * MS_PER_DAY, (lastChange - from) * TAIL_SHARE);
  const position = (time: number) => ((time - from) / (to - from)) * 100;
  const lanes = Math.max(...bars.map((bar) => bar.lane)) + 1;
  const statuses = STATUS_ORDER.filter((status) => bars.some((bar) => bar.status === status));

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <div className="relative flex flex-col gap-1.5">
          {Array.from({ length: lanes }, (_, lane) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lanes are positions
            <div className="relative h-9" key={lane}>
              {bars
                .filter((bar) => bar.lane === lane)
                .map((bar) => {
                  const left = position(utcDayMs(bar.start));
                  const right = bar.end ? position(utcDayMs(bar.end)) : 100;
                  const isSelected = bar.indices.some((index) => selected.includes(index));
                  return (
                    <Tooltip key={bar.indices.join(',')}>
                      <TooltipTrigger asChild>
                        <button
                          aria-label={`Edit schedule: ${describeBar(bar)}`}
                          aria-pressed={isSelected}
                          className={cn(
                            '@container absolute inset-y-0 flex min-w-3 cursor-pointer items-center gap-1.5 overflow-hidden rounded-md border px-2 text-xs font-medium tabular-nums transition-[box-shadow,filter] hover:brightness-95 dark:hover:brightness-125',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            STATUS_META[bar.status].className,
                            !bar.end && 'rounded-r-none border-r-0',
                            isSelected && bars.length > 1 && 'ring-2 ring-primary',
                          )}
                          onClick={() => onSelect(bar.indices[0])}
                          style={{ left: `${left}%`, width: `${Math.max(right - left, 0)}%` }}
                          type="button"
                        >
                          <span className="hidden whitespace-nowrap @[8rem]:inline">
                            {bar.start}
                            {bar.sessioned ? ' · C/E/W' : ''}
                          </span>
                          {bar.end ? null : <ArrowRight aria-hidden className="ml-auto hidden size-3.5 shrink-0 @[3rem]:block" />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">{describeBar(bar)}</TooltipContent>
                    </Tooltip>
                  );
                })}
            </div>
          ))}
        </div>
        <div aria-hidden className="pointer-events-none absolute -inset-y-1 w-px bg-foreground/60" style={{ left: `${position(today)}%` }} />
      </div>
      <div aria-hidden className="relative h-4 text-[11px] text-muted-foreground tabular-nums">
        {monthTicks(from, to)
          .filter((month) => position(month) > TICK_EDGE_PERCENT && position(month) < 100 - TICK_EDGE_PERCENT)
          .map((month, index) => (
            <span className="absolute -translate-x-1/2 whitespace-nowrap" key={month} style={{ left: `${position(month)}%` }}>
              {formatUtcMonth(month, index === 0 || new Date(month).getUTCMonth() === 0)}
            </span>
          ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {statuses.map((status) => (
          <li className="inline-flex items-center gap-1.5" key={status}>
            <span aria-hidden className={cn('size-2.5 rounded-sm border', STATUS_META[status].className)} />
            {STATUS_META[status].label}
          </li>
        ))}
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-3 w-px bg-foreground/60" />
          Today
        </li>
      </ul>
    </div>
  );
};
ScheduleTimeline.displayName = 'ScheduleTimeline';
